import { forwardRef } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import type { Invoice, BillingSettings } from "@/types/billing";

interface InvoicePDFProps {
  invoice: Invoice;
  settings?: Partial<BillingSettings>;
}

export const InvoicePDF = forwardRef<HTMLDivElement, InvoicePDFProps>(
  ({ invoice, settings }, ref) => {
    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    };

    const logoUrl = settings?.logo_url || "/perseo/logoMagma.png";
    const bankName = settings?.bank_name || "BANCO SANTANDER";
    const bankIban = settings?.bank_iban || "ES41 0049 2313 6025 1426 1409";

    return (
      <div
        ref={ref}
        className="bg-white text-black"
        style={{
          width: "210mm",
          minHeight: "297mm",
          fontFamily: "Arial, sans-serif",
          fontSize: "9pt",
          padding: "10mm 15mm",
          position: "relative",
        }}
      >
        {/* HEADER - Logo y datos empresa izquierda, Cliente derecha (para sobre con ventana) */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8mm" }}>
          {/* Izquierda: Logo + Datos empresa */}
          <div style={{ maxWidth: "55%" }}>
            <img
              src={logoUrl}
              alt="Logo"
              style={{ height: "18mm", marginBottom: "3mm" }}
              crossOrigin="anonymous"
            />
            <div style={{ fontWeight: "bold", fontSize: "14pt" }}>
              {settings?.company_name || "MAGMA COMPOSITES S.L."}
            </div>
            <div style={{ fontSize: "9pt", color: "#333" }}>
              {settings?.address_line1 || "Pol. Ind. Cueva San José II, Nave nº8"}
            </div>
            <div style={{ fontSize: "9pt", color: "#333" }}>
              {settings?.postal_code || "44570"} {settings?.city || "CALANDA"}
            </div>
            <div style={{ fontSize: "9pt", color: "#333" }}>
              {settings?.province || "TERUEL"}
            </div>
            <div style={{ fontSize: "9pt", color: "#333" }}>
              <strong>NIF/CIF:</strong> {settings?.tax_id || "ESB63222715"}
            </div>
            <div style={{ fontSize: "9pt", color: "#333" }}>
              Teléfono: {settings?.phone || "978877816"}
            </div>
          </div>

          {/* Derecha: Recuadro datos cliente (posición para ventana de sobre) */}
          <div
            style={{
              border: "1px solid #000",
              padding: "4mm",
              minWidth: "70mm",
              maxWidth: "80mm",
            }}
          >
            <div style={{ fontWeight: "bold", fontSize: "11pt", marginBottom: "2mm" }}>
              {invoice.customer_name}
            </div>
            <div style={{ fontSize: "9pt" }}>
              <strong>NIF/CIF:</strong> {invoice.customer_tax_id}
            </div>
            <div style={{ fontSize: "9pt", whiteSpace: "pre-line", marginTop: "2mm" }}>
              {invoice.customer_address}
            </div>
          </div>
        </div>

        {/* SECCIÓN DOCUMENTO */}
        <div style={{ display: "flex", gap: "5mm", marginBottom: "5mm" }}>
          <table style={{ borderCollapse: "collapse", fontSize: "9pt" }}>
            <thead>
              <tr style={{ backgroundColor: "#f0f0f0" }}>
                <th style={{ border: "1px solid #000", padding: "2mm 4mm" }}>Documento</th>
                <th style={{ border: "1px solid #000", padding: "2mm 4mm" }}>Número</th>
                <th style={{ border: "1px solid #000", padding: "2mm 4mm" }}>Página</th>
                <th style={{ border: "1px solid #000", padding: "2mm 4mm" }}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: "1px solid #000", padding: "2mm 4mm", fontWeight: "bold" }}>
                  {invoice.invoice_type === "proforma" ? "Proforma" :
                   invoice.invoice_type === "rectificativa" ? "Rectificativa" : "Factura"}
                </td>
                <td style={{ border: "1px solid #000", padding: "2mm 4mm" }}>
                  {invoice.invoice_number || "BORRADOR"}
                </td>
                <td style={{ border: "1px solid #000", padding: "2mm 4mm", textAlign: "center" }}>1</td>
                <td style={{ border: "1px solid #000", padding: "2mm 4mm" }}>
                  {format(new Date(invoice.issue_date), "dd/MM/yyyy")}
                </td>
              </tr>
            </tbody>
          </table>
          <div style={{ marginLeft: "auto", fontSize: "9pt" }}>
            <strong>Número de proveedor:</strong>
          </div>
        </div>

        {/* DATOS DE PAGO */}
        <div style={{ marginBottom: "5mm", border: "1px solid #ccc", padding: "3mm" }}>
          <div style={{ backgroundColor: "#f5f5f5", padding: "2mm", marginBottom: "2mm", fontWeight: "bold" }}>
            Datos de pago
          </div>
          <div style={{ display: "flex", gap: "10mm", fontSize: "9pt" }}>
            <div>{invoice.payment_method || "Transferencia bancaria"}</div>
            <div><strong>{bankName}</strong></div>
            <div><strong>IBAN:</strong> {bankIban}</div>
          </div>
        </div>

        {/* TABLA DE ARTÍCULOS */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "5mm", fontSize: "9pt" }}>
          <thead>
            <tr style={{ backgroundColor: "#d32f2f", color: "white" }}>
              <th style={{ border: "1px solid #000", padding: "2mm", textAlign: "left", width: "15%" }}>Artículo</th>
              <th style={{ border: "1px solid #000", padding: "2mm", textAlign: "left", width: "35%" }}>Descripción</th>
              <th style={{ border: "1px solid #000", padding: "2mm", textAlign: "right", width: "10%" }}>Cantidad</th>
              <th style={{ border: "1px solid #000", padding: "2mm", textAlign: "right", width: "12%" }}>Precio Ud.</th>
              <th style={{ border: "1px solid #000", padding: "2mm", textAlign: "right", width: "12%" }}>Subtotal</th>
              <th style={{ border: "1px solid #000", padding: "2mm", textAlign: "right", width: "8%" }}>Descuento</th>
              <th style={{ border: "2px solid #d32f2f", padding: "2mm", textAlign: "right", width: "12%", backgroundColor: "#fff", color: "#000" }}>TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lines || []).map((line: any, idx: number) => (
              <tr key={line.id || idx}>
                <td style={{ border: "1px solid #ccc", padding: "2mm" }}>{line.product_code || line.reference || ""}</td>
                <td style={{ border: "1px solid #ccc", padding: "2mm" }}>
                  {line.description}
                  {line.notes && <div style={{ fontSize: "8pt", color: "#666" }}>{line.notes}</div>}
                </td>
                <td style={{ border: "1px solid #ccc", padding: "2mm", textAlign: "right" }}>{formatCurrency(line.quantity)}</td>
                <td style={{ border: "1px solid #ccc", padding: "2mm", textAlign: "right" }}>{formatCurrency(line.unit_price)}</td>
                <td style={{ border: "1px solid #ccc", padding: "2mm", textAlign: "right" }}>{formatCurrency(line.quantity * line.unit_price)}</td>
                <td style={{ border: "1px solid #ccc", padding: "2mm", textAlign: "right" }}>{line.discount_percent > 0 ? line.discount_percent + "%" : ""}</td>
                <td style={{ border: "2px solid #d32f2f", padding: "2mm", textAlign: "right", fontWeight: "bold" }}>{formatCurrency(line.subtotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* DESGLOSE IVA */}
        <table style={{ width: "60%", borderCollapse: "collapse", marginBottom: "5mm", fontSize: "9pt" }}>
          <thead>
            <tr style={{ backgroundColor: "#f0f0f0" }}>
              <th style={{ border: "1px solid #000", padding: "2mm" }}>Tipo</th>
              <th style={{ border: "1px solid #000", padding: "2mm" }}>Importe</th>
              <th style={{ border: "1px solid #000", padding: "2mm" }}>Descuento</th>
              <th style={{ border: "1px solid #000", padding: "2mm" }}>Pronto pago</th>
              <th style={{ border: "1px solid #000", padding: "2mm" }}>Base</th>
              <th style={{ border: "1px solid #000", padding: "2mm" }}>IVA</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.tax_breakdown || [{ tax_rate: 21, base_amount: invoice.subtotal, tax_amount: invoice.subtotal * 0.21 }]).map((tax: any, idx: number) => (
              <tr key={idx}>
                <td style={{ border: "1px solid #ccc", padding: "2mm", textAlign: "center" }}>{tax.tax_rate}</td>
                <td style={{ border: "1px solid #ccc", padding: "2mm", textAlign: "right" }}>{formatCurrency(tax.base_amount || invoice.subtotal)}</td>
                <td style={{ border: "1px solid #ccc", padding: "2mm", textAlign: "right" }}></td>
                <td style={{ border: "1px solid #ccc", padding: "2mm", textAlign: "right" }}></td>
                <td style={{ border: "1px solid #ccc", padding: "2mm", textAlign: "right" }}>{formatCurrency(tax.base_amount || invoice.subtotal)}</td>
                <td style={{ border: "1px solid #ccc", padding: "2mm", textAlign: "right" }}>{formatCurrency(tax.tax_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* TOTAL EURO */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "5mm" }}>
          <div
            style={{
              backgroundColor: "#000000",
              color: "white",
              padding: "3mm 8mm",
              fontWeight: "bold",
              fontSize: "14pt",
              display: "flex",
              gap: "15mm",
            }}
          >
            <span>TOTAL EURO</span>
            <span>{formatCurrency(invoice.total_amount)}</span>
          </div>
        </div>

        {/* OBSERVACIONES */}
        <div style={{ marginBottom: "8mm" }}>
          <div style={{ backgroundColor: "#000000", color: "white", padding: "2mm 4mm", fontWeight: "bold" }}>
            Observaciones
          </div>
          <div style={{ border: "1px solid #ccc", borderTop: "none", padding: "3mm", minHeight: "15mm", fontSize: "9pt" }}>
            {invoice.notes || ""}
            {invoice.payment_method && <div>FORMA DE PAGO {invoice.payment_method.toUpperCase()}</div>}
          </div>
        </div>

        {/* PIE LEGAL */}
        <div style={{ fontSize: "7pt", color: "#666", lineHeight: 1.4, borderTop: "1px solid #ccc", paddingTop: "3mm" }}>
          <p style={{ marginBottom: "2mm" }}>
            En cumplimiento de lo dispuesto en el artículo 13 de Reglamento (UE) 2016/679 relativo a la protección de las personas físicas en lo que respecta al tratamiento de datos personales y a la libre circulación de estos datos y al. 11 Ley orgánica 3/2018, de 5 de diciembre, de Protección de datos Personales y garantía de los derechos digitales le informamos que {settings?.company_name || "MAGMA COMPOSITES S.L."}, con CIF {settings?.tax_id || "B63222715"} y con domicilio en {settings?.address_line1 || "Pol. Ind. Cueva San José II, nave 8"} {settings?.city || "Calanda"} {settings?.postal_code || "44570"} ({settings?.province || "Teruel"}) es el responsable del tratamiento y que dicho tratamiento se lleva a cabo con la finalidad de la gestión administrativa-contable, el cumplimiento de la relación contractual existente y posibles acciones comerciales, siendo su consentimiento nuestra base legal para ello.
          </p>
          <p style={{ marginBottom: "2mm" }}>
            Asimismo, le informamos de la posibilidad que tiene de ejercer todos sus derechos sobre tratamiento de sus datos de carácter personal dirigiéndose por escrito al email francisco.marin@magmacomposites.com con el asunto "Protección de Datos", a dicha solicitud deberá acompañar una fotocopia de su D.N.I o documento equivalente.
          </p>
          <p style={{ marginBottom: "2mm" }}>
            Por la gestión de los productos envasados incluidos en la presente factura que ha puesto en el mercado, {settings?.company_name || "Magma Composites S.L."} ha aportado al sistema colectivo de responsabilidad ampliada del productor (SCRAP) GENCI la cantidad resultante de aplicar a tales productos, según corresponda, las tarifas disponibles en el siguiente enlace: www.genci.es/tarifas
          </p>
          <p>
            Número de productor de producto ENV/2023/000016568<br />
            Inscrita en el Reg. Mercantil de Barcelona, Tomo 35695, Hoja B276750
          </p>
        </div>
      </div>
    );
  }
);

InvoicePDF.displayName = "InvoicePDF";
